import { createNoise3D } from 'simplex-noise';
import { initNavigation } from './src/navigation.js';

// Initialize navigation
initNavigation();

// Vector helper class
class Vector {
    constructor(x, y) {
        this.x = x || 0;
        this.y = y || 0;
    }

    setAngle(angle) {
        const length = this.getLength();
        this.x = Math.cos(angle) * length;
        this.y = Math.sin(angle) * length;
    }

    getAngle() {
        return Math.atan2(this.y, this.x);
    }

    setLength(length) {
        const angle = this.getAngle();
        this.x = Math.cos(angle) * length;
        this.y = Math.sin(angle) * length;
    }

    getLength() {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }

    addTo(v) {
        this.x += v.x;
        this.y += v.y;
    }
}

class FlowField {
    settings = {
        frequency: 0.2,
    }
    
    constructor(w, h, noiseFunction, settings = {}) {
        this.settings = { ...this.settings, ...settings };
        this.w = w || 10;
        this.h = h || 10;
        this.time = 0;
        this.noise3D = noiseFunction;
        this.build();
    }
    
    build() {
        this.cols = Math.ceil(this.w);
        this.rows = Math.ceil(this.h);
        this.field = new Array(this.cols);
        for (let x = 0; x < this.cols; x ++) {
            this.field[x] = new Array(this.rows);
            for (let y = 0; y < this.rows; y ++) {
                this.field[x][y] = new Vector(0, 0);
            }
        }
    }
    
    update(delta) {
        this.time += delta;
        const updateTime = this.time * this.settings.frequency / 1000;
        for (let x = 0; x < this.field.length; x ++) {
            for (let y = 0; y < this.field[x].length; y ++) {
                const angle = this.noise3D(x/20, y/20, updateTime) * Math.PI * 2;
                const length = this.noise3D(x/10 + 40000, y/10 + 40000, updateTime);
                
                const vec = this.field[x][y];
                // Setting values directly to avoid any zero-length issues in Vector methods
                vec.x = Math.cos(angle) * length;
                vec.y = Math.sin(angle) * length;
                
                if (typeof this.manipulateVector === 'function') {
                    this.manipulateVector(vec, x, y);
                }
                
                if (typeof this.onDraw === 'function') {
                    this.onDraw(vec, x, y);
                }
            }
        }
    }
}

// SETUP
const settings = { frequency: 0.08 }; // Slightly reduced frequency for smoothness
const isMobile = window.innerWidth < 768;
const tileSize = isMobile ? 80 : 40; // Double tile size on mobile (4x fewer calculations)
const tileRatio = 2;

const canvas = document.getElementById('flowfield');
if (canvas) {
    const container = canvas.parentElement;
    const ctx = canvas.getContext('2d', { alpha: false }); // Better performance
    const noiseFunction = createNoise3D();

    let ff, ctxScale, heightColorScaling;
    const mouse = new Vector(-100, -100); // Start off-screen

    function init() {
        const box = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        // Limit resolution for performance, especially on high-DPI mobile
        const scaleLimit = isMobile ? Math.min(dpr, 1.5) : dpr;
        
        canvas.width = box.width * scaleLimit;
        canvas.height = (box.width * (container.clientHeight / container.clientWidth)) * scaleLimit;

        const cols = Math.ceil(container.clientWidth / tileSize);
        const rows = Math.ceil(container.clientHeight / (tileSize * tileRatio));

        ctxScale = {
            x: canvas.width / cols,
            y: canvas.height / rows,
        }
        heightColorScaling = 255 / rows;

        ff = new FlowField(cols, rows, noiseFunction, settings);

        ff.onDraw = (vector, x, y) => {
            if (x === 0 && y === 0) {
                // background draw instead of clear for better perf
                ctx.fillStyle = '#0a1012'; 
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
            const xmove = vector.getLength() * Math.abs(vector.x);
            const ymove = vector.getLength() * Math.abs(vector.y);
            const red = Math.round((-20 * xmove) + (80 * ymove) + (50 - (0.6 * y * heightColorScaling)));
            const green = Math.round((180 * xmove) + (20 * ymove) - 60 + (0.4 * y * heightColorScaling));
            const blue = Math.round((50 * xmove) + (30 * ymove) + (40 - (0.5 * y * heightColorScaling)) + (0.5 * y * heightColorScaling));
            
            ctx.fillStyle = `rgba(${red}, ${green}, ${blue}, 0.8)`;
            ctx.fillRect(x * ctxScale.x, y * ctxScale.y, ctxScale.x + 1, ctxScale.y + 1); 
        }

        ff.manipulateVector = (vector, x, y) => {
            // Mouse interaction only if not mobile for better perf and UX
            if (isMobile) return;
            
            const pos = new Vector((x * ctxScale.x) + (0.5 * ctxScale.x), (y * ctxScale.y) + (0.5 * ctxScale.y));
            const rect = canvas.getBoundingClientRect();
            const localMouseX = (mouse.x - rect.left) / (rect.width / canvas.width);
            const localMouseY = (mouse.y - rect.top) / (rect.height / canvas.height);
            const mouseEffect = new Vector((localMouseX - pos.x) / canvas.width, (localMouseY - pos.y) / canvas.height);
            vector.addTo(mouseEffect);
            if (vector.getLength() > 1) {
                const angle = Math.atan2(vector.y, vector.x);
                vector.x = Math.cos(angle);
                vector.y = Math.sin(angle);
            }
        }
    }

    init();

    let lastStep = 0;
    let throttleTimer = false;
    
    // Scroll velocity tracking with damping
    let scrollVelocity = 0;
    let lastScrollY = window.scrollY;
    let currentBoost = 0;

    window.addEventListener('scroll', () => {
        const currentScrollY = window.scrollY;
        const diff = Math.abs(currentScrollY - lastScrollY);
        // Increase velocity based on scroll delta
        scrollVelocity += diff * 0.15; 
        lastScrollY = currentScrollY;
    }, { passive: true });

    function step(time) {
        // Apply friction to velocity
        scrollVelocity *= 0.94;
        
        // Target boost based on velocity
        const targetBoost = Math.min(scrollVelocity / 8, 6); 
        // Smooth transition to target boost
        currentBoost += (targetBoost - currentBoost) * 0.1;
        
        if (ff) {
            const delta = (time - lastStep || 0) * (1 + currentBoost);
            ff.update(delta);
        }
        
        lastStep = time;
        window.requestAnimationFrame(step);
    }
    window.requestAnimationFrame(step);

    if (!isMobile) {
        window.addEventListener('mousemove', (e) => {
            mouse.x = e.clientX;
            mouse.y = e.clientY;
        });
    }

    window.addEventListener('resize', () => {
        if (throttleTimer) return;
        throttleTimer = true;
        setTimeout(() => {
            init();
            throttleTimer = false;
        }, 250);
    });
}


// Direction-aware Liquid Button Logic
document.body.addEventListener('mouseenter', (e) => {
    const btn = e.target.closest('.btn-liquid, .btn-big, .btn-premium, .btn-cta, .btn-pill, .btn-audit');
    if (btn && btn.querySelector('.liquid-bg')) {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        btn.style.setProperty('--x', `${x}px`);
        btn.style.setProperty('--y', `${y}px`);
    }
}, true);
